import { useCallback, useEffect } from "react";
import Button from "components/Button";
import Input from "components/Input";
import Dialog from "components/Dialog";
import React from "react";
import Dinero from "dinero.js";
import { useForm } from "react-hook-form";
import ReactTooltip from 'react-tooltip';
import * as Yup from "yup";
import { ChevronDown, PaperUpload, Upload } from "react-iconly";
import JWT, { RefferalVia, RefferalWidrawnValues } from "api/referral/users";
import { useApp } from "./app";

type IProps = {
  otherClasses?: string;
  currentBalance?: string;
  closeAction: () => void;
  onSucessfulWithdrawRequest: () => void;
};

type PrimaryButtonProps = {
  buttonText: string;
  className?: string;
  action: () => void;
};
interface IFormValues {
  paymentMethod: RefferalVia;
  email: string;
  amount: string;
  iban: string;
  file: File;
}

const WithdrawModal: React.FC<IProps> = ({
  otherClasses,
  closeAction,
  currentBalance,
  onSucessfulWithdrawRequest
}) => {
  const { notify } = useApp();
  const [paymentMethod, setPaymentMethod] =
    React.useState<RefferalVia | null>();
  const [creditsValue, setCreditsValue] = React.useState<number>(0);
  const [wireValue, setWireValue] = React.useState<number>(0);
  const [withdrawOption, setWithdrawOption] = React.useState<string>("custom");
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [loadingSaveCampaign, setLoadingSaveCampaign] =
    React.useState<boolean>(false);
  const [file, setFile] = React.useState<any>();
  const [step, setStep] = React.useState<number>(1);
  const [tooltipCredits, setTooltipCredits] = React.useState<boolean>(false);
  const [tooltipWire, setTooltipWire] = React.useState<boolean>(false);
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("This is not a valid email address")
      .when("paymentMethod", {
        is: RefferalVia.credits,
        then: Yup.string().required("Field is mandatory"),
      }),
    amount: Yup.number()
    .required("Field is mandatory")
    .positive()
    .min(1, 'The minimum amount is one'),
    confirmationCode: Yup.string()
      .when("stepForm", {
        is: '2',
        then: Yup.string().required("Field is mandatory"),
      }),
  });

  const useYupValidationResolver = (schema: any) =>
    useCallback(
      async (data: any) => {
        try {
          const values = await schema.validate(data, {
            abortEarly: false,
          });

          return {
            values,
            errors: {},
          };
        } catch (errors: any) {
          const r = {
            values: {},
            errors: errors.inner.reduce(
              (allErrors: any, currentError: any) => ({
                ...allErrors,
                [currentError.path]: {
                  type: currentError.type ?? "validation",
                  message: currentError.message,
                },
              }),
              {}
            ),
          };

          return r;
        }
      },
      [schema]
    );

  const resolver = useYupValidationResolver(validationSchema);
  const {
    register,
    getValues,
    setError,
    formState,
    control,
    setValue,
    errors,
    handleSubmit,
  } = useForm<IFormValues>({
    resolver,
  });

  const paymentMethods = [
    {
      imageSrc: "/images/wiretransfer.svg",
      title: "Wire Transfer",
      subtitle: "",
      selector: RefferalVia.wire,
      available: true,
      tooltipText: 'wire'
    },
    {
      imageSrc: "/images/blindspot-credits.svg",
      title: "Blindspot Credits",
      subtitle: "Get 25% extra value",
      selector: RefferalVia.credits,
      available: true,
      tooltipText: "credits"
    },
  ];

  const withdrawOptions = [
    {
      title: "Custom amount",
      selector: "custom",
    },
    {
      title: "The whole amount",
      selector: "whole",
    },
  ];
  useEffect(() => {
    if (withdrawOption === "whole") {
      setValue(
        "amount",
        Dinero({
          amount: Number(currentBalance),
          precision: 4,
          currency: "USD",
        })
          .toFormat()
          .replace("$", ""),
        {
          shouldValidate: false,
          shouldDirty: false,
        }
      );
      if (currentBalance)
      setCreditsValue(Math.round((Number(currentBalance) + (Number(currentBalance) * 0.25))))
      setWireValue(Math.round((Number(currentBalance))))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawOption]);

  const primaryButton: PrimaryButtonProps = {
    buttonText: "Withdraw",
    action: async () => {
      handleSubmit(onSubmit);
    },
  };

  const secondaryButton: PrimaryButtonProps = {
    buttonText: "Cancel",
    action: () => {
      setShowDialog(false);
    },
  };

  const onSubmit = async (data: any) => {
    if (loadingSaveCampaign) return false;
    if (step === 1 && paymentMethod) {
      const values: RefferalWidrawnValues = {
        via: paymentMethod,
        amount: data.amount * 10000,
        details: {
          email: data.email,
          iban: data.iban,
        },
      };
        const codeResponse = await JWT.sendRefferalWithdrawCode(values);
        if(codeResponse && codeResponse.all === 'good') {
          setStep(2);
          setValue("stepForm", 2);
          notify('We sent you an email with the confirmation code for withdraw request!', "success", "top-right");
          return;
        }
        if(!codeResponse.status) {
          notify(codeResponse.errorMessage, "error", "top-right");
        }
    }

    if (step === 2 && paymentMethod && data.confirmationCode) {
      const valuesStep2: RefferalWidrawnValues = {
        via: paymentMethod,
        amount: data.amount * 10000,
        code: data.confirmationCode,
        details: {
          email: data.email,
          iban: data.iban,
        },
      };
        const codeResponse = await JWT.sendRefferalWithdrawRequest(valuesStep2);
        if(codeResponse && codeResponse.all === 'good') {
          onSucessfulWithdrawRequest();
          notify("Withdrawal request successful", "success", "top-right");
          closeAction();
          return;
        }
        if(!codeResponse.status) {
          notify(codeResponse.errorMessage, "error", "top-right");
        }
      }
  };

  const { dirtyFields } = formState;
  const getValidation = (inputName: any) => {
    const val = {
      isDirty: (dirtyFields as any)[inputName],
      error: (setError as any)[inputName],
    };
    return val;
  };

  const handleChange = (event: any) => {
    setFile(event.target.files[0]);

    setValue("file", event.target.files, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onChangePaymentMethod = (element: any) => {
    if (element && element.available && step === 1) {
      setPaymentMethod(element.selector);
    }
  }

  return (
    <div
      className={`bg-dark-200 rounded-20 max-w-[679px] ${otherClasses ? otherClasses : ""}`}
    >
      {tooltipCredits && (
        <ReactTooltip id='tooltip-payment-method' place="top" className="custom-tooltip">
          <h1>
            If you choose to transfer your balance to <strong>Blindspot Credits</strong>, 
            these will be credited in your account and you will be able to use that amount 
            to advertise on <strong>over 500.000 digital billboards</strong>  all across the world. 
            <strong> And you get 25% extra value for money!</strong>
            
          </h1>
        </ReactTooltip>
      )}
      {tooltipWire && (
        <ReactTooltip id='tooltip-payment-method' place="top" className="custom-tooltip">
          <h1>
            After submitting the withdraw request, we will send you an email with all the necessary details to complete the withdraw process!
          </h1>
        </ReactTooltip>
      )}
      {showDialog && (
        <Dialog
          key="1"
          title="Final Check"
          loading={loadingSaveCampaign}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          description={`You're about to withdraw <strong>${Dinero({
            amount: Number(currentBalance),
            precision: 4,
            currency: "USD",
          }).toFormat()}</strong>, to <strong>${paymentMethod}!</strong><br><br/>An email will be sent to your email address. 
        <br><br/>Please confirm withdrow from your email!`}
        />
      )}
      <h1 className="mb-3 text-xl text-white fonst-semibold">
        Thanks for sharing Blindspot to the world!
      </h1>
      <h1 className="text-dark-300 mb-7">
        Your current value of your balance is{" "}
        <span className="font-semibold text-white">
          {Dinero({
            amount: Number(currentBalance),
            precision: 4,
            currency: "USD",
          }).toFormat()}
        </span>
        . Choose your preferred payment method. 
      </h1>

      <form
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-row tablet:flex-col tablet:space-y-4 mb-7">
          <h1 className="w-4/12 text-white fonst-semibold">Withdraw to</h1>
          <div className="flex flex-row justify-start w-full tablet:flex-col tablet:space-y-4">
            {paymentMethods.map((el: any, index: number) => {
              return (
                <div
                  key={`${index}_${el.selector}`}
                  className={`${
                    paymentMethod === el.selector
                      ? "border border-primarydark-default"
                      : "border border-dark-200"
                  } ${el.available ? 'cursor-pointer' : ''} relative bg-dark-400 bg-opacity-20 h-24 w-40 tablet:w-full rounded-20 p-4 flex flex-col justify-between mr-4 tablet:mr-0`}
                  onClick={() => {
                    setValue("paymentMethod", el.selector, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    onChangePaymentMethod(el);
                  }}
                >
                  {el.tooltipText && (
                    <div 
                      className="info-mark" data-tip data-for='tooltip-payment-method'
                      onMouseLeave={()=> { el.tooltipText === 'wire' ? setTooltipWire(false) : setTooltipCredits(false)}}
                      onMouseEnter={()=> { el.tooltipText === 'wire' ? setTooltipWire(true) : setTooltipCredits(true)}}
                    >i</div>
                  )}
                  <div className="flex flex-row items-start justify-between">
                    <img src={el.imageSrc} />
                    <div
                      className={`${
                        paymentMethod === el.selector
                          ? "border-2 border-primarydark-default"
                          : ""
                      } rounded-full bg-white w-4 h-4 flex items-center justify-center`}
                    >
                      <div
                        className={`${
                          paymentMethod === el.selector
                            ? "bg-primarydark-default"
                            : ""
                        } rounded-full bg-white w-2 h-2`}
                      ></div>
                    </div>
                  </div>
                  <div className="flex flex-row flex-nowrap">
                    <h1 className="text-xs font-semibold mr-1">{el.subtitle}</h1>
                  </div>
                  <h1 className="text-xs">{el.title}</h1>
                </div>
              );
            })}
          </div>
        </div>
        <div className="hidden">
          <Input
            name="paymentMethod"
            type="hidden"
            innerRef={register}
            validation={getValidation("paymentMethod")}
            placeholder="Insert your email"
            label=""
            fullWidth={true}
          />
          <Input
            name="stepForm"
            type="hidden"
            innerRef={register}
            validation={getValidation("stepForm")}
            placeholder="Insert your email"
            label=""
            fullWidth={true}
          />
        </div>
        {paymentMethod && (
          <>
            <div className="mb-7">{paymentMethod === RefferalVia.wire ? 
              <div className="text-dark-300">
                After submitting the withdraw request, we will send you an email with all the necessary details to complete the withdraw process!
              </div>
              :
              <h1 className="text-dark-300">
                If you choose to transfer your balance to <strong>Blindspot Credits</strong>, 
                these will be credited in your account and you will be able to use that amount 
                to advertise on <strong>over 500.000 digital billboards</strong>  all across the world. 
                <strong> And you get 25% extra value for money!</strong>
              </h1>
            }
            </div>
            <div
              className={` flex-row tablet:flex-col tablet:space-y-4 mb-7 ${
                paymentMethod === RefferalVia.wire ? "hidden" : ""
              } `}
            >
              <h1 className="w-4/12 mt-3 text-white fonst-semibold tablet:w-full">
                Add email
              </h1>
              <div className="flex flex-row justify-start w-full">
                <Input
                  name="email"
                  type="email"
                  innerRef={register}
                  validation={getValidation("email")}
                  placeholder="Insert Blindspot account email to receive credits"
                  label=""
                  fullWidth={true}
                  readOnly={step === 2}
                />
              </div>
            </div>
            <div className="flex flex-row mb-7 tablet:flex-col tablet:space-y-4">
              <h1 className="w-4/12 mt-3 text-white fonst-semibold tablet:w-full">
                Amount to withdraw
              </h1>
              <div className="flex flex-col justify-start w-full">
                <div className="flex flex-row tablet:flex-col tablet:space-y-4 mb-7">
                  {withdrawOptions.map((el: any, index: number) => {
                    return (
                      <div
                        key={`${index}_${el.selector}`}
                        className={`${
                          withdrawOption === el.selector
                            ? "border border-primarydark-default"
                            : ""
                        } px-3 py-2 bg-dark-400 bg-opacity-20 flex flex-row items-center justify-arround mr-3 rounded-2xl cursor-pointer`}
                        onClick={() => {
                           if (step === 1) {
                             setWithdrawOption(el.selector)
                           }
                          }
                        }
                      >
                        <div
                          className={`${
                            withdrawOption === el.selector
                              ? "border-2 border-primarydark-default"
                              : ""
                          } rounded-full bg-white w-4 h-4 mr-3 flex items-center justify-center`}
                        >
                          <div
                            className={`${
                              withdrawOption === el.selector
                                ? "bg-primarydark-default"
                                : ""
                            } rounded-full bg-white w-2 h-2`}
                          ></div>
                        </div>
                        {el.title}
                      </div>
                    );
                  })}
                </div>
                <div className="relative">
                  <Input
                    name="amount"
                    type="number"
                    step="0.01"
                    min="1"
                    validation={getValidation("amount")}
                    placeholder="Insert amount to withdraw"
                    label=""
                    innerRef={register}
                    fullWidth={true}
                    readOnly={withdrawOption === "whole" || step === 2 ? true : false}
                    onChange={(el: any) => {
                      const val = Number(el.target.value);
                      setCreditsValue(Math.round((val + (val * 0.25)) * 10000))
                      setWireValue(Math.round((val) * 10000))
                    }}
                  />
                  {paymentMethod === RefferalVia.credits ? (
                      <div className="absolute top-2 right-0">
                        (~ 
                        {
                          Dinero({
                            amount: Number(creditsValue),
                            precision: 4,
                            currency: "USD",
                          }).toFormat()
                        } 
                        {' '}credits)
                      </div> 
                    )
                    : 
                    (
                      <div className="absolute top-2 right-0">
                        ({
                          Dinero({
                            amount: Number(wireValue),
                            precision: 4,
                            currency: "USD",
                          }).toFormat()
                        })
                      </div> 
                    )
                  }
                </div>
              </div>
            </div>
            {step === 2 && (
              <div className="flex flex-row mb-7 tablet:flex-col tablet:space-y-4">
                <h1 className="w-4/12 mt-3 text-white fonst-semibold tablet:w-full">
                  Confirmation code
                </h1>
                <div className="flex flex-col justify-start w-full">
                  <Input
                    name="confirmationCode"
                    type="text"
                    validation={getValidation("confirmationCode")}
                    placeholder="Insert confirmation code"
                    label=""
                    innerRef={register}
                    fullWidth={true}
                  />
                </div>
              </div>
            )}
            {/* {paymentMethod === RefferalVia.wire && (
            <div className="flex flex-row tablet:flex-col tablet:space-y-4 mb-7">
              <h1 className="w-4/12 mt-3 text-white fonst-semibold tablet:w-full">
                Proof of payment
              </h1>
              <div className="relative flex flex-row justify-start w-full">
                <input
                  className="hidden"
                  type="file"
                  name="file"
                  id="fileUpload"
                  ref={register}
                  onChange={handleChange}
                  accept="image/*,.pdf"
                  title="Upload a file"
                />
                <label
                  htmlFor="fileUpload"
                  className="w-full p-2 border border-dashed cursor-pointer rounded-2xl border-grey-default border-opacity-80 text-dark-300"
                >
                  {file?.name && (
                    <div className="px-2 bg-dark-300 bg-opacity-40 w-fit rounded-xl">
                      {file.name}
                    </div>
                  )}
                  {!file?.name && <h1>Upload a file ...</h1>}
                </label>
                <label
                  htmlFor="fileUpload"
                  className="absolute text-white cursor-pointer right-2 top-2"
                >
                  <Upload set="broken" />
                </label>
              </div>
              <div
                className={`text-danger-500 w-fit text-xs pt-2 pl-3.5 space-x-1 collapsible-error ${
                  getValidation("file") ? "expanded" : "hidden"
                }`}
              >
                <p className="inline">{getValidation("file").error}</p>
              </div>
            </div> 
            )} */}
          </>
        )}
        <div className="flex flex-row tablet:flex-col tablet:space-y-4">
          <Button
            className="mr-4 tablet:order-last tablet:mt-4"
            type="submit"
            color="secondary"
            fullWidth={true}
            onClick={closeAction}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            fullWidth={true}
          >
            Withdraw
          </Button>
        </div>
      </form>
    </div>
  );
};
export default WithdrawModal;
